import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  css`
    :host {
      display: block;
    }

    /* ── Base layout ─────────────────────────────────────────────────────────── */
    .uwc-msg {
      display: flex;
      align-items: flex-start;
      gap: ${t.space3};
      /* Compact vertical padding (scales with the size presets) so the alert
         reads dense at XS and roomy at XL. */
      padding: ${t.space2} ${t.space4};
      border-radius: ${t.radiusMd};
      background: var(--_bg, #e0f2fe);
      border: var(--_border, none);
      animation: uwc-msg-in ${t.durationBase} ease-out both;
    }

    /* ── Severity modifiers ──────────────────────────────────────────────────── */
    .uwc-msg--info {
      --_bg:           var(--uwc-message-info-bg,          #e0f2fe);
      --_color:        var(--uwc-message-info-color,       #0369a1);
      --_border-color: var(--uwc-message-info-border,      #38bdf8);
    }

    .uwc-msg--success {
      --_bg:           var(--uwc-message-success-bg,       #dcfce7);
      --_color:        var(--uwc-message-success-color,    #166534);
      --_border-color: var(--uwc-message-success-border,   #22c55e);
    }

    .uwc-msg--warn {
      --_bg:           var(--uwc-message-warn-bg,          #fef9c3);
      --_color:        var(--uwc-message-warn-color,       #b45309);
      --_border-color: var(--uwc-message-warn-border,      #f59e0b);
    }

    .uwc-msg--error {
      --_bg:           var(--uwc-message-error-bg,         #fee2e2);
      --_color:        var(--uwc-message-error-color,      #b91c1c);
      --_border-color: var(--uwc-message-error-border,     #ef4444);
    }

    .uwc-msg--secondary {
      --_bg:           var(--uwc-message-secondary-bg,     #f1f5f9);
      --_color:        var(--uwc-message-secondary-color,  #475569);
      --_border-color: var(--uwc-message-secondary-border, #94a3b8);
    }

    .uwc-msg--contrast {
      --_bg:           var(--uwc-message-contrast-bg,      #1e293b);
      --_color:        var(--uwc-message-contrast-color,   #f8fafc);
      --_border-color: var(--uwc-message-contrast-border,  #475569);
    }

    /* ── Variant modifiers ───────────────────────────────────────────────────── */

    /* filled (default) — tinted bg, no border */
    .uwc-msg--filled {
      --_border: none;
      background: var(--_bg);
    }

    /* outlined — surface bg, 1px colored border */
    .uwc-msg--outlined {
      background: var(--uwc-message-outlined-bg, var(--uwc-surface, #ffffff));
      --_border: 1px solid var(--_border-color);
      border: 1px solid var(--_border-color);
    }

    /* simple — transparent, no border */
    .uwc-msg--simple {
      background: transparent;
      --_border: none;
      border: none;
      padding-left: 0;
      padding-right: 0;
    }

    /* ── Icon area ───────────────────────────────────────────────────────────── */
    .uwc-msg__icon-wrap {
      flex-shrink: 0;
      margin-top: 1px;
      /* Derive from the md font token (×1.25 ≈ the old 1.1rem at the default
         scale) so the 1em <uwc-icon> scales with the size presets / font slider.
         A hardcoded rem here froze the icon at ~17.6px while the text scaled. */
      font-size: calc(${t.fontSizeMd} * 1.25);
      color: var(--_color);
      display: flex;
      align-items: center;
    }

    .uwc-msg__icon {
      color: var(--_color);
    }

    /* ── Body ────────────────────────────────────────────────────────────────── */
    .uwc-msg__body {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: ${t.space1};
    }

    .uwc-msg__text {
      font-weight: ${t.fontWeightSemibold};
      font-size: ${t.fontSizeMd};
      color: var(--_color);
      line-height: 1.4;
    }

    .uwc-msg__detail {
      font-size: ${t.fontSizeMd};
      color: var(--_color);
      opacity: 0.85;
      line-height: 1.5;
    }

    /* ── Close button ────────────────────────────────────────────────────────── */
    .uwc-msg__close {
      flex-shrink: 0;
      margin-left: auto;
      background: none;
      border: none;
      cursor: pointer;
      opacity: 0.6;
      padding: 2px;
      border-radius: ${t.radiusSm};
      color: var(--_color);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: opacity ${t.durationBase}, background ${t.durationBase};
    }

    .uwc-msg__close:hover {
      opacity: 1;
      background: color-mix(in oklab, var(--_color) 12%, transparent);
    }

    .uwc-msg__close:focus-visible {
      outline: 2px solid var(--_color);
      outline-offset: 2px;
    }

    /* ── Animations ──────────────────────────────────────────────────────────── */
    @keyframes uwc-msg-in {
      from {
        opacity: 0;
        transform: scale(0.97);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }

    @keyframes uwc-msg-out {
      from {
        opacity: 1;
        transform: scale(1);
      }
      to {
        opacity: 0;
        transform: scale(0.97);
      }
    }

    .uwc-msg--closing {
      animation: uwc-msg-out ${t.durationBase} ease-in forwards;
    }

    /* ── Reduced motion ──────────────────────────────────────────────────────── */
    @media (prefers-reduced-motion: reduce) {
      .uwc-msg {
        animation: none;
      }
      .uwc-msg--closing {
        animation: none;
        opacity: 0;
      }
    }
  `,
];
